// packages/web/src/hooks/useWishlist.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { wishlistService } from '@/services/wishlist.service';
import { productService} from '@/services/product.service';
import { toast } from 'sonner';
import { Product } from '@bookstore/shared';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Get user's wishlist product IDs
 */
export const useWishlistIds = () => {
  const { firebaseUser, loading } = useAuth();
  const user = firebaseUser;

  return useQuery<string[]>({
    queryKey: ['wishlist-ids', user?.uid],
    queryFn: () => wishlistService.getWishlist(user!.uid),
    enabled: !!user,
    staleTime: 30 * 1000,
    placeholderData: [], //     Assume empty wishlist initially
  });
};

/**
 * Get full product details for wishlist items
 */
export const useWishlistProducts = () => {
  const { firebaseUser, loading } = useAuth();
  const user = firebaseUser;
  const { data: wishlistIds, isLoading: idsLoading } = useWishlistIds();

  return useQuery<Product[]>({
    queryKey: ['wishlist-products', user?.uid, wishlistIds],
    queryFn: async () => {
      if (!wishlistIds || wishlistIds.length === 0) {
        return [];
      }

      // Fetch all products in parallel, catching errors for deleted products
      const productPromises = wishlistIds.map((productId) =>
        productService.getProductById(productId).catch(err => {
          console.warn(`Failed to fetch product ${productId}:`, err.message);
          return null;
        })
      );

      const products = await Promise.all(productPromises);

      // Track deleted products to auto-clean
      const invalidProductIds: string[] = [];
      const validProducts: Product[] = [];

      products.forEach((product, index) => {
        if (product !== null) {
          validProducts.push(product);
        } else {
          invalidProductIds.push(wishlistIds[index]);
          console.warn(`Product ${wishlistIds[index]} no longer exists, removing from wishlist`);
        }
      });

      // Auto-clean invalid products from wishlist in background
      if (invalidProductIds.length > 0 && user?.uid) {
        Promise.all(
          invalidProductIds.map(productId => 
            wishlistService.removeFromWishlist(user.uid, productId).catch(err => 
              console.error('Failed to remove invalid product from wishlist:', err)
            )
          )
        ).then(() => {
          toast.info(`Removed ${invalidProductIds.length} unavailable item(s) from wishlist`);
        });
      }

      return validProducts;
    },
    enabled: !!user && !!wishlistIds && wishlistIds.length > 0,
    staleTime: 2 * 60 * 1000,
    placeholderData: [], // Assume empty wishlist initially
  });
};




/**
 * Add product to wishlist
 */
export const useAddToWishlist = () => {
  const { firebaseUser, loading } = useAuth();
  const user = firebaseUser;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => wishlistService.addToWishlist(user!.uid, productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist-ids'] });
      queryClient.invalidateQueries({ queryKey: ['wishlist-products'] });
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      toast.success('Added to wishlist');
    },
    onError: () => {
      toast.error('Failed to add to wishlist');
    },
  });
};

/**
 * Remove product from wishlist
 */
export const useRemoveFromWishlist = () => {
  const { firebaseUser, loading } = useAuth();
  const user = firebaseUser;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => wishlistService.removeFromWishlist(user!.uid, productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist-ids'] });
      queryClient.invalidateQueries({ queryKey: ['wishlist-products'] });
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      toast.success('Removed from wishlist');
    },
    onError: () => {
      toast.error('Failed to remove from wishlist');
    },
  });
};

/**
 * Toggle wishlist (add/remove)
 */
export const useToggleWishlist = () => {
  const { firebaseUser, loading } = useAuth();
  const user = firebaseUser;
  const { data: wishlistIds } = useWishlistIds();
  const addMutation = useAddToWishlist();
  const removeMutation = useRemoveFromWishlist();

  return {
    toggle: (productId : string) => {
      if (!user) {
        toast.error('Please login to add to wishlist');
        return;
      }

      const isInWishlist = wishlistIds?.includes(productId);

      if (isInWishlist) {
        removeMutation.mutate(productId);
      } else {
        addMutation.mutate(productId);
      }
    },
    isInWishlist: (productId: string) => wishlistIds?.includes(productId) || false,
    isPending: addMutation.isPending || removeMutation.isPending,
  };
};


/**
 * Main hook - Simple API like Zustand with optimistic updates
 */
export const useWishlist = () => {
  const { firebaseUser, loading } = useAuth();
  const user = firebaseUser;
  const { data: wishlistIds = [] } = useWishlistIds();
  const queryClient = useQueryClient();

  // Add to wishlist with optimistic update
  const addMutation = useMutation({
    mutationFn: (productId: string) => wishlistService.addToWishlist(user!.uid, productId),

    // Optimistic update - instant UI feedback
    onMutate: async (productId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['wishlist-ids', user?.uid] });

      // Snapshot previous value
      const previousWishlist = queryClient.getQueryData<string[]>(['wishlist-ids', user?.uid]);

      // Optimistically update
      queryClient.setQueryData<string[]>(['wishlist-ids', user?.uid], (old = []) => {
        return old.includes(productId) ? old : [...old, productId];
      });

      return { previousWishlist };
    },

    onError: (err, productId, context) => {
      // Rollback on error
      if (context?.previousWishlist) {
        queryClient.setQueryData(['wishlist-ids', user?.uid], context.previousWishlist);
      }
      toast.error('Failed to add to wishlist');
    },

    onSuccess: () => {
      // Refetch to ensure sync
      queryClient.invalidateQueries({ queryKey: ['wishlist-ids', user?.uid] });
      queryClient.invalidateQueries({ queryKey: ['wishlist-products', user?.uid] });
      toast.success('Added to wishlist');
    },
  });

  // Remove from wishlist with optimistic update
  const removeMutation = useMutation({
    mutationFn: (productId: string) => wishlistService.removeFromWishlist(user!.uid, productId),

    // Optimistic update
    onMutate: async (productId) => {
      await queryClient.cancelQueries({ queryKey: ['wishlist-ids', user?.uid] });

      const previousWishlist = queryClient.getQueryData<string[]>(['wishlist-ids', user?.uid]);

      queryClient.setQueryData<string[]>(['wishlist-ids', user?.uid], (old = []) => {
        return old.filter((id) => id !== productId);
      });

      return { previousWishlist };
    },

    onError: (err, productId, context) => {
      if (context?.previousWishlist) {
        queryClient.setQueryData(['wishlist-ids', user?.uid], context.previousWishlist);
      }
      toast.error('Failed to remove from wishlist');
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist-ids', user?.uid] });
      queryClient.invalidateQueries({ queryKey: ['wishlist-products', user?.uid] });
      toast.success('Removed from wishlist');
    },
  });

  // Optimistic update - Zustand-like API
  return {
    // State
    wishlistIds,
    itemCount: wishlistIds.length,

    // Checkers
    isInWishlist: (productId: string) => wishlistIds.includes(productId),

    // Actions
    addToWishlist: (productId: string) => {
      if (!user) {
        toast.error('Please login to add to wishlist');
        return;
      }
      addMutation.mutate(productId);
    },

    removeFromWishlist: (productId: string) => {
      if (!user) return;
      removeMutation.mutate(productId);
    },

    toggleWishlist: (productId : string) => {
      if (!user) {
        toast.error('Please login to add to wishlist');
        return;
      }

      const isInList = wishlistIds.includes(productId);
      if (isInList) {
        removeMutation.mutate(productId);
      } else {
        addMutation.mutate(productId);
      }
    },

    // Loading states
    isPending: addMutation.isPending || removeMutation.isPending,
  };
};

/**
 * Clear entire wishlist
 */
export const useClearWishlist = () => {
  const { firebaseUser, loading } = useAuth();
  const user = firebaseUser;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => wishlistService.clearWishlist(user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist-ids'] });
      queryClient.invalidateQueries({ queryKey: ['wishlist-products'] });
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      toast.success('Wishlist cleared');
    },
    onError: () => {
      toast.error('Failed to clear wishlist');
    },
  });
};